import { computed, ref, type Ref, watch } from 'vue'
import { useRouter } from 'vue-router'
// @ts-ignore
import { isEmail } from 'sisu/js/validate-email'
import { validate } from '@/js/helpers/validate-fns'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import stockStatus from '@/js/helpers/stockStatus'
import type { BuyRequest } from '@/js/services/types/order'

let guestBinEmail = ''

export function useBackInStock(buyRequest: BuyRequest) {
  const customerStore = useCustomerStore()
  const productStore = useProductStore()
  const rogue = useRogue()
  const router = useRouter()

  const BIN = ref({
    showEmail : false,
    loading   : false,
    signedUp  : false,
    email     : '',
  })

  const loggedIn = computed(() => customerStore.loggedIn)
  const bins = computed(() => customerStore.bins)
  const email = computed(() => customerStore.email)

  const binProductId = computed(() => {
    try {
      return getSimpleForBuyRequest(buyRequest, rogue)?.productId
    } catch (e) {
      return undefined
    }
  })

   
  const existingBin = computed(() => bins.value.some((bin: { product_id: number }) => bin.product_id === binProductId.value))

  watch(existingBin, (val) => {
    if (val) {
      BIN.value.signedUp = true
    }
  })

  // Centralized: hide email input whenever status is not eligible for BIN
  const status = computed(() => {
    try {
      return stockStatus.forBuyRequest(buyRequest as any, productStore.products)
    } catch (e) {
      return ''
    }
  })

  watch(status, (newVal) => {
    if (newVal !== 'bin') {
      BIN.value.showEmail = false
    }
  })

  async function clickBinButton(el: HTMLElement) {
    if (BIN.value.signedUp) {
      if (loggedIn.value) {
        // If they are a customer, send them to their BINs page
        router.push('/account/bin')
      }

      // @todo Give guests some kind of action here?
      return
    }

    // Dont resubmit if already loading
    if (BIN.value.loading) {
      return
    }

    if (!loggedIn.value && !BIN.value.showEmail) {
      BIN.value.showEmail = true
      BIN.value.email = guestBinEmail

      return
    }

    // For guests, check that an email has been entered
    if (!loggedIn.value && BIN.value.showEmail && !validate(el, false)) {
      return
    }

    const binEmail = BIN.value.email || email.value || ''

    if (!binEmail || !isEmail(binEmail)) {
      BIN.value.showEmail = true

      return
    }

    // Start submitting the BIN
    BIN.value.loading = true

    // The BIN needs to know the parent
    // @todo Is this correct? what about simples in configs in groups/bundles? copied from existing code
    let parentId = buyRequest.metadata?.groupedProductId
    if (!parentId && buyRequest.children?.length) {
      parentId = buyRequest.productId
    }

    try {
      // Send the actual bin signup
      await customerStore.binSignup(binEmail, binProductId.value!, parentId, rogue)
      // After success, show the spinner for .5 sec
      setTimeout(() => {
        BIN.value.loading = false
        BIN.value.signedUp = true
        BIN.value.showEmail = false
        // Guests dont have a BIN array, so we need to set the status var here
        if (!loggedIn.value) {
          guestBinEmail = BIN.value.email
        }
        rogue.track.track({ type: 'binSignup', items: [buyRequest], email: binEmail })
      }, 500)
    } catch (e) {
      BIN.value.loading = false
      BIN.value.showEmail = true
      rogue.log.error('Error signing up for bin', e as Error)
    }
  }

  return { BIN, clickBinButton }
}
